import { SimpleGrid } from '@chakra-ui/react';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { t } from '@lingui/core/macro';
import { Suspense } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

import type { RouterOutputs } from '@duke-hq/svc-api/provider';
import useFiltersContext from '@duke-hq/filters/context/useFiltersContext';

import useDashboardContext from '../../context/useDashboardContext';
import ErrorFallback from '../ErrorFallback';
import CardGroup from './shared/CardGroup';
import Section from './shared/Section';
import StatCard from './shared/StatCard';

type Props = {
  data?: RouterOutputs['dashboard']['getRevisions'];
};

function RevisionsUI({ data }: Props) {
  const { openPanel } = useDashboardContext();
  const { overOneWeek, thisWeek } = data ?? {};

  return (
    <SimpleGrid columns={{ base: 1, md: 1, xl: 2 }} spacing="8px">
      <StatCard
        backgroundColour="bg.canvas"
        title={t`+1 week ago`}
        titleColour="fg.muted"
        description={t`Outstanding`}
        descriptionColour="danger.emphasis"
        value={overOneWeek}
        valueColour="fg.emphasis"
        onClick={() => openPanel('Revisions', { timeframe: 'OVER_ONE_WEEK' })}
      />
      <StatCard
        backgroundColour="bg.canvas"
        title={t`This week`}
        titleColour="fg.muted"
        description={t`Outstanding`}
        descriptionColour="yellow.500"
        value={thisWeek}
        valueColour="fg.emphasis"
        onClick={() => openPanel('Revisions', { timeframe: 'THIS_WEEK' })}
      />
    </SimpleGrid>
  );
}

function RevisionsWithData() {
  const { filters } = useFiltersContext();
  const { data } = trpcTenant.dashboard.getRevisions.useQuery(
    { filters },
    {
      suspense: true,
      trpc: {
        context: {
          skipBatch: true,
        },
      },
    },
  );

  if (!data || Object.keys(data).length === 0) {
    return <ErrorFallback />;
  }

  return <RevisionsUI data={data} />;
}

export default function Revisions() {
  const { openPanel } = useDashboardContext();

  return (
    <Section
      title={t`Revisions`}
      tooltipText={t`Assessments with revisions requested that have yet to be resubmitted`}
    >
      <CardGroup
        cardType="muted"
        title={t`Revisions requested`}
        openPanel={() => openPanel('Revisions')}
      >
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <Suspense fallback={<RevisionsUI />}>
            <RevisionsWithData />
          </Suspense>
        </ErrorBoundary>
      </CardGroup>
    </Section>
  );
}
